import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { buildScopedApiUrl, getApiHeaders } from './config';
import { buildTradeServiceUrl } from './tradeServiceRouting';
import { useWorkspace } from './workspace/WorkspaceContext';

const modalAppElement = document.getElementById('root');
if (modalAppElement) {
  Modal.setAppElement(modalAppElement);
}

function SignalFlyTradeModal({ isOpen, closeModal }) {
  const { workspace } = useWorkspace();
  const [formData, setFormData] = useState({
    tradeOption: '',
    side: 'open_long',
    margin: '1',
    noOfCoins: '10',
    takeProfitPercentage: '200',
    stopLossPercentage: '100',
    isRepelTrade: 'false',
    symbols: {},
    exchange: workspace.exchange,
    marketType: workspace.marketType,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [showDropdown, setShowDropdown] = useState(true);
  const [tradeOption, setTradeOption] = useState(null);
  const [performersData, setPerformersData] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('topPriceIncrease');
  const [selectedCoins, setSelectedCoins] = useState([]);
  const [randomCoinSelections, setRandomCoinSelections] = useState([]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      exchange: workspace.exchange,
      marketType: workspace.marketType,
    }));
  }, [workspace]);

  const handleRandomCoinSelection = (event) => {
    const selectedOptions = [...event.target.selectedOptions].map((o) => o.value);
    const updatedSelections = selectedOptions.map((coin) => {
      return randomCoinSelections.find((sel) => sel.coin === coin) || { coin, side: 'open_long' };
    });
    setRandomCoinSelections(updatedSelections);
  };

  const handleRandomCoinSideChange = (coin, side) => {
    setRandomCoinSelections((prevSelections) =>
      prevSelections.map((sel) =>
        sel.coin === coin ? { ...sel, side } : sel
      )
    );
  };

  const fetchCryptoDayPerformers = async () => {
    try {
      const response = await fetch(
        buildScopedApiUrl('/api/bot/ui/post/cryptoDayPerformanceData', workspace),
        { headers: getApiHeaders() }
      );
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      if (data && typeof data === 'object') {
        setPerformersData(data);
        const uniqueCoins = new Set();
        Object.values(data).flat().forEach((coin) => uniqueCoins.add(coin));
        setSelectedCoins(Array.from(uniqueCoins));
      } else {
        setPerformersData({});
        setSelectedCoins([]);
      }
    } catch (fetchError) {
      setError(fetchError.message);
      setPerformersData({});
      setSelectedCoins([]);
    }
  };

  useEffect(() => {
    fetchCryptoDayPerformers();
  }, [workspace]);

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setSelectedCategory(category);
    setSelectedCoins(performersData[category] || []);
  };

  const handleTradeOptionChange = (e) => {
    const newTradeOption = e.target.value;
    setTradeOption(newTradeOption);
    setFormData((prevFormData) => ({
      ...prevFormData,
      tradeOption: newTradeOption,
    }));

    if (newTradeOption === 'TradeRandomCoin') {
      const categoryCoins = performersData[selectedCategory] || [];
      const updatedSelections = categoryCoins.map((coin) => {
        return randomCoinSelections.find((sel) => sel.coin === coin) || { coin, side: 'open_long' };
      });
      setRandomCoinSelections(updatedSelections);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const toggleTPInputType = () => {
    setShowDropdown(!showDropdown);
    if (!showDropdown) {
      setFormData((prev) => ({ ...prev, takeProfitPercentage: '200' }));
    }
  };

  const toggleSLInputType = () => {
    setShowDropdown(!showDropdown);
    if (!showDropdown) {
      setFormData((prev) => ({ ...prev, stopLossPercentage: '100' }));
    }
  };

  const submitTradeRequest = (path, submittedData) => {
    setLoading(true);
    let tradeUrl;
    try {
      tradeUrl = buildTradeServiceUrl(path, workspace);
    } catch (routingError) {
      setError(routingError.message);
      setLoading(false);
      return;
    }
    fetch(tradeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getApiHeaders(),
      },
      body: JSON.stringify({
        ...submittedData,
        exchange: workspace.exchange,
        marketType: workspace.marketType,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((data) => {
        setSubmissionResult(data);
        setLoading(false);
      })
      .catch((submitError) => {
        setError(submitError.message);
        setLoading(false);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let updatedFormData = { ...formData };

    if (tradeOption === 'TradeRandomCoin') {
      const symbolsMap = randomCoinSelections.reduce((acc, current) => {
        acc[current.coin] = current.side;
        return acc;
      }, {});
      updatedFormData.symbols = symbolsMap;
    }

    if (tradeOption === 'TradeUpwardCoin') {
      submitTradeRequest('/api/bot/ui/post/tradeUpwardCoins', updatedFormData);
    } else if (tradeOption === 'TradeDownWardCoin') {
      submitTradeRequest('/api/bot/ui/post/tradeDownwardCoins', updatedFormData);
    } else if (tradeOption === 'TradeMostStableCoin') {
      submitTradeRequest('/api/bot/ui/post/tradeMostStableCoins', updatedFormData);
    } else if (tradeOption === 'TradeRandomCoin') {
      submitTradeRequest('/api/bot/ui/post/tradeRandomCoins', updatedFormData);
    } else {
      submitTradeRequest('/api/bot/ui/post/tradeUpwardCoins', updatedFormData);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      contentLabel="Repel Trade Symbol"
      className="ReactModal__Content modal-content-custom"
      overlayClassName="ReactModal__Overlay"
    >
      <button className="modal-close-button" onClick={closeModal}>&times;</button>
      <h2 className="page-title">Signal Fly Trade</h2>
      <p style={{ marginTop: 0, color: '#666' }}>
        Workspace: <strong>{workspace.exchange}</strong> / <strong>{workspace.marketType}</strong>
      </p>
      <br />
      {loading && <div className="loading-message">Processing...</div>}
      {error && <div>Error: {error}</div>}
      {submissionResult && <div>Result: {JSON.stringify(submissionResult)}</div>}
      {!loading && !submissionResult && (
        <form className="modal-form" onSubmit={handleSubmit}>
          <label>
            Today Performers:
            <select
              name="category"
              value={selectedCategory}
              onChange={handleCategoryChange}
            >
              {Object.keys(performersData).map((category, index) => (
                <option key={index} value={category}>{category}</option>
              ))}
            </select>
          </label>
          <label>
            Coins:
            <select
              name="coins"
              value={selectedCoins}
              readOnly
            >
              {selectedCoins.map((coin, index) => (
                <option key={index} value={coin}>{coin}</option>
              ))}
            </select>
          </label>

          <label>
            Trade Option:
            <select
              name="tradeOption"
              value={formData.tradeOption}
              defaultValue={'TradeUpwardCoin'}
              onChange={handleTradeOptionChange}
            >
              <option value="TradeUpwardCoin">Trade Upward Coins</option>
              <option value="TradeDownWardCoin">Trade Downward Coins</option>
              <option value="TradeMostStableCoin">Trade Most Stable Coins</option>
              <option value="TradeRandomCoin">Trade Random Coins</option>
            </select>
          </label>

          {tradeOption === 'TradeRandomCoin' && (
            <>
              <label>
                Select Coins:
                <select
                  multiple
                  value={randomCoinSelections.map((sel) => sel.coin)}
                  onChange={handleRandomCoinSelection}
                >
                  {selectedCoins.map((coin, index) => (
                    <option key={index} value={coin}>{coin}</option>
                  ))}
                </select>
              </label>
              {randomCoinSelections.map((selection, index) => (
                <div key={index}>
                  {selection.coin}
                  <select
                    value={selection.side}
                    onChange={(e) => handleRandomCoinSideChange(selection.coin, e.target.value)}
                  >
                    <option value="open_long">Long</option>
                    <option value="open_short">Short</option>
                  </select>
                </div>
              ))}
            </>
          )}

          {tradeOption !== 'TradeRandomCoin' && (
            <>
              <label>
                Side:
                <select
                  name="side"
                  value={formData.side}
                  onChange={handleInputChange}
                >
                  <option value="open_long">Long</option>
                  <option value="open_short">Short</option>
                </select>
              </label>
              <label>
                No of Coins:
                <input
                  type="number"
                  name="noOfCoins"
                  max="10"
                  min="1"
                  defaultValue={10}
                  value={formData.noOfCoins}
                  onChange={handleInputChange}
                />
              </label>
            </>
          )}

          <label>
            Margin:
            <input
              type="number"
              name="margin"
              max="5"
              min="1"
              defaultValue={1}
              value={formData.margin}
              onChange={handleInputChange}
            />
          </label>
          <label>
            TP Percentage:
            {showDropdown ? (
              <select
                name="takeProfitPercentage"
                value={formData.takeProfitPercentage}
                defaultValue={200}
                onChange={handleInputChange}
              >
                <option value="50">50%</option>
                <option value="100">100%</option>
                <option value="150">150%</option>
                <option value="200">200%</option>
              </select>
            ) : (
              <input
                type="number"
                name="takeProfitPercentage"
                value={formData.takeProfitPercentage}
                onChange={handleInputChange}
                max="300"
                required
              />
            )}
            <button type="button" onClick={toggleTPInputType}>
              {showDropdown ? 'Type Manually' : 'Select '}
            </button>
          </label>

          <label>
            SL Percentage:
            {showDropdown ? (
              <select
                name="stopLossPercentage"
                value={formData.stopLossPercentage}
                defaultValue={100}
                onChange={handleInputChange}
              >
                <option value="50">50%</option>
                <option value="100">100%</option>
                <option value="150">150%</option>
                <option value="200">200%</option>
              </select>
            ) : (
              <input
                type="number"
                name="stopLossPercentage"
                value={formData.stopLossPercentage}
                onChange={handleInputChange}
                max="200"
                required
              />
            )}
            <button type="button" onClick={toggleSLInputType}>
              {showDropdown ? 'Type Manually' : 'Select '}
            </button>
          </label>

          <label>
            Repel Trade:
            <select
              name="isRepelTrade"
              value={formData.isRepelTrade}
              defaultValue={false}
              onChange={handleInputChange}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </label>
          <div className="modal-button-container">
            <button type="submit">Submit</button>
            <button type="button" onClick={closeModal}>Cancel</button>
          </div>
        </form>
      )}
    </Modal>
  );
}

export default SignalFlyTradeModal;
